import { createTheme, Theme } from '@mui/material/styles';

/**
 * Theme Configuration
 * 
 * Creates Material-UI theme with ios26 style for both light and dark modes.
 * Maintains consistent design language with gradient accents, rounded corners,
 * and subtle borders across both themes.
 */

export const createAppTheme = (mode: 'light' | 'dark'): Theme => {
  const isDark = mode === 'dark';

  return createTheme({
    palette: {
      mode,
      primary: {
        main: '#00B4FF',
        light: '#4fc9ff',
        dark: '#008ac3',
      },
      secondary: {
        main: '#8A2BE2',
        light: '#a85bf0',
        dark: '#6a1fb0',
      },
      background: {
        default: isDark ? '#000000' : '#F5F5F7',
        paper: isDark ? '#0B0B0B' : '#FFFFFF',
      },
      text: {
        primary: isDark ? '#FFFFFF' : '#1D1D1F',
        secondary: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
      },
    },
    typography: {
      fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      h1: {
        fontSize: '2.5rem',
        fontWeight: 500,
      },
      h2: {
        fontSize: '2rem',
        fontWeight: 500,
      },
      h3: {
        fontSize: '1.75rem',
        fontWeight: 500,
      },
      h4: {
        fontSize: '1.5rem',
        fontWeight: 500,
      },
      h5: {
        fontSize: '1.25rem',
        fontWeight: 500,
      },
      h6: {
        fontSize: '1rem',
        fontWeight: 500,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: isDark ? '#000000' : '#F5F5F7',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: 8,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: isDark ? '#0B0B0B' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
            border: isDark 
              ? '1px solid rgba(138,43,226,0.25)' 
              : '1px solid rgba(138,43,226,0.15)',
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: isDark ? '#0B0B0B' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
            boxShadow: isDark 
              ? '0 10px 25px rgba(0, 180, 255, 0.12)' 
              : '0 10px 25px rgba(0, 180, 255, 0.08)',
            border: isDark 
              ? '1px solid rgba(0,180,255,0.18)' 
              : '1px solid rgba(0,180,255,0.12)',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0B0B0B' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
            border: isDark 
              ? '1px solid rgba(138,43,226,0.25)' 
              : '1px solid rgba(138,43,226,0.15)',
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0B0B0B' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
            border: isDark 
              ? '1px solid rgba(138,43,226,0.25)' 
              : '1px solid rgba(138,43,226,0.15)',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: {
            backgroundColor: isDark ? '#0B0B0B' : '#FFFFFF',
            backgroundImage: 'none',
            color: isDark ? '#FFFFFF' : '#1D1D1F',
            border: isDark 
              ? '1px solid rgba(138,43,226,0.25)' 
              : '1px solid rgba(138,43,226,0.15)',
          },
        },
      },
    },
  });
};

// Default export for backward compatibility (dark theme)
export const theme = createAppTheme('dark'); 